import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordBuilder } from "@/components/mt/ChordBuilder";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory5() {
  const { lesson, course, accent } = findLesson("music-theory", "5");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT5-Chord-Theory-Animated.mp4"
        caption="Triads — stack two thirds: M3 / m3 / dim / aug"
        hint="≈ 23 sec · watch first"
      />
      <Chapter number="05" eyebrow="Music Theory · Chapter Five" title="Chords: the harmony beneath the melody">
        <p>
          A pianist cannot survive on melody alone. A flutist or a singer can — they only have
          one note at a time to think about. But a piano gives you ten fingers, and the
          expectation is that some of them are doing harmony while others handle the tune. That
          harmony lives, almost entirely, inside one big idea: <strong>chords</strong>.
        </p>
        <MTQuote>
          We have to focus on harmony, rhythm as well as the melody. So harmony is a very,
          very important part of our playing, and under harmony comes this very important
          chapter called as chords.
        </MTQuote>
        <p>
          A chord is a stack of notes played together. The simplest useful stack is three notes
          — a <em>triad</em> — built by skipping every other note of a scale. Pick a starting
          note, skip one, take the next, skip one, take the next. Three notes. Done.
        </p>
        <NoteImage
          src="/notes/music-theory/finals/05a-major-minor-dim-aug-chord-formulas.png"
          caption="Jason's note: every triad shape on the same root C — Major (1 3 5), Minor (1 ♭3 5), Diminished (1 ♭3 ♭5), Augmented (1 3 ♯5), Sus2 and Sus4. The right-hand column gives the note names so you can see the shape on the keyboard."
        />
      </Chapter>

      <Chapter eyebrow="The recipe" title="Triads are 1 · 3 · 5 of the scale">
        <Prose>
          <p>
            Take any major scale and write its seven notes in a circle. Then on each note,
            "skip-and-grab" twice: skip the next, grab the one after. Skip the next, grab the
            one after. The three notes you end up holding form a <strong>triad</strong> built
            on that scale degree.
          </p>
        </Prose>

        <TheoryBox label="Triad-building rule">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>Pick a note · skip one · grab · skip one · grab</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (the three notes you collected = the triad on that degree)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            On <strong>G major</strong> — <span className="font-mono">G A B C D E F♯</span> —
            that gives:
          </p>
        </Prose>

        <Steps>
          <Step><strong>From G:</strong> G · skip A · B · skip C · D &nbsp;→&nbsp; <span className="font-mono text-amber-300">G–B–D</span> (G major)</Step>
          <Step><strong>From A:</strong> A · skip B · C · skip D · E &nbsp;→&nbsp; <span className="font-mono text-amber-300">A–C–E</span> (A minor)</Step>
          <Step><strong>From B:</strong> B · skip C · D · skip E · F♯ &nbsp;→&nbsp; <span className="font-mono text-amber-300">B–D–F♯</span> (B minor)</Step>
          <Step><strong>From C:</strong> C · skip D · E · skip F♯ · G &nbsp;→&nbsp; <span className="font-mono text-amber-300">C–E–G</span> (C major)</Step>
          <Step><strong>From D:</strong> D · skip E · F♯ · skip G · A &nbsp;→&nbsp; <span className="font-mono text-amber-300">D–F♯–A</span> (D major)</Step>
          <Step><strong>From E:</strong> E · skip F♯ · G · skip A · B &nbsp;→&nbsp; <span className="font-mono text-amber-300">E–G–B</span> (E minor)</Step>
          <Step><strong>From F♯:</strong> F♯ · skip G · A · skip B · C &nbsp;→&nbsp; <span className="font-mono text-amber-300">F♯–A–C</span> (F♯ diminished)</Step>
        </Steps>

        <Prose>
          <p>
            Seven scale degrees → seven triads. Notice that some came out happy, some came out
            sad, and one came out plain weird. That distribution is not an accident — it's
            baked into the formula of the major scale itself.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The three flavours" title="Major · minor · diminished">
        <Prose>
          <p>
            Triads come in three emotional flavours, determined by the size of the gaps between
            their three notes:
          </p>
        </Prose>

        <Glossary>
          <Term name="Major" def="Bright, victorious, positive. Built on degrees 1, 4, and 5 of any major scale." />
          <Term name="Minor" def="Pensive, melancholic, introspective. Built on degrees 2, 3, and 6." />
          <Term name="Diminished" def="Tense, anxious, unresolved. Built on degree 7 only — the weird one." />
        </Glossary>

        <Prose>
          <p>
            The 1, 4, and 5 chords are the <em>happy</em> chords — they sound victorious or
            playful or just plain good. The 2, 3, and 6 chords are the <em>sad</em> chords —
            they sound like loss, contemplation, or distance. The 7 chord, the diminished, is the
            <em> chaotic</em> one — anxious, scary, unresolved, used sparingly but powerfully.
          </p>
        </Prose>

        <ChordBuilder root="C" families={["triad"]} />
      </Chapter>

      <Chapter eyebrow="Roman numerals" title="The way musicians actually talk about chords">
        <Prose>
          <p>
            Just as Chapter 2 used numbers to label scale degrees, theory uses
            <strong> Roman numerals</strong> to label chord positions in a key. Capital numerals
            for major chords, lowercase for minor, and a small circle for diminished. So in any
            major key, the seven diatonic chords are:
          </p>
        </Prose>

        <TheoryBox label="The seven chords of any major key">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>I &nbsp; ii &nbsp; iii &nbsp; IV &nbsp; V &nbsp; vi &nbsp; vii°</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (capital = major, lowercase = minor, ° = diminished)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            On G major: <span className="font-mono text-amber-300">G — Am — Bm — C — D — Em — F♯°</span>.
            On C major: <span className="font-mono text-amber-300">C — Dm — Em — F — G — Am — B°</span>.
            On any major key, the same Roman-numeral pattern applies: I, IV, V are major; ii,
            iii, vi are minor; vii° is diminished. Always. The recipe is universal.
          </p>
        </Prose>

        <ChordTree tonic="G" mode="major" />
      </Chapter>

      <Chapter eyebrow="Why the pattern repeats" title="The 3 / 3 / 1 distribution">
        <Prose>
          <p>
            The "three majors, three minors, one diminished" distribution comes directly from the
            tone-and-semitone shape of the major scale. The intervals between consecutive
            scale-tones aren't equal — some are whole-steps and some are half-steps — and that
            unevenness is what gives each triad its quality. You don't have to memorise which
            chord is which: the formula does it for you, every time.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>I, IV, V</strong> (1st, 4th, 5th degrees) → <strong>major</strong></li>
            <li><strong>ii, iii, vi</strong> (2nd, 3rd, 6th degrees) → <strong>minor</strong></li>
            <li><strong>vii°</strong> (7th degree) → <strong>diminished</strong></li>
          </ul>
        </Prose>

        <Prose>
          <p>
            These seven chords are the harmonic skeleton of a huge number of major-key songs.
            Pop, rock, classical, film scores, hymns, nursery rhymes — all of them lean on this
            tree.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="A first taste of seventh chords" title="Stack one more third">
        <Prose>
          <p>
            Once you have a triad, the next stack-up is to add one more "skip-and-grab". That
            fourth note — a 7th above the root — turns a triad into a <strong>seventh chord</strong>.
            Add the <em>major</em> 7th and you get a major-seventh chord: lush, floating,
            sophisticated. Add the <em>minor</em> 7th to that same major triad and you get a
            <em>dominant</em> seventh: restless, urgent, desperate to resolve. Seventh chords are
            the language of jazz, soul, R&B, and gospel.
          </p>
          <p>
            We will spend more time on sevenths in later chapters. For now, just listen to how
            the sound thickens when you add that fourth note:
          </p>
        </Prose>

        <ChordBuilder root="C" families={["triad", "seventh"]} />
      </Chapter>

      <Chapter eyebrow="From chords to progressions" title="What you do with this next">
        <Prose>
          <p>
            Knowing the seven triads of a key is half the work. The other half is putting them
            in a sequence — a <em>chord progression</em> — that tells a story. Common
            progressions like <strong>I–V–vi–IV</strong> (the "axis of awesome" pop sequence),
            <strong> ii–V–I</strong> (the spine of jazz), and <strong>I–IV–I–V</strong> (the
            12-bar blues skeleton) are nothing more than ordered choices from the seven chords
            you just built.
          </p>
          <p>
            And once you have a progression, the way you <em>play</em> the chords — tightly
            voiced or spread out, on the beat or syncopated, blocked or arpeggiated — is what
            turns theory into music. That's the work of the chapters to come.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Homework" title="Build the chords of one major scale after another">
        <Steps>
          <Step>
            <strong>Write G major in a circle.</strong> Then walk skip-and-grab around the circle
            and write all seven triads. Label each one I, ii, iii, IV, V, vi, vii°.
          </Step>
          <Step>
            <strong>Repeat for C, D, A, E, F, B♭, E♭.</strong> Use whichever order feels
            comfortable; the goal is to have done it more than once.
          </Step>
          <Step>
            <strong>Play each chord set on the keyboard.</strong> Right hand plays the triad;
            left hand plays the bass note (the chord's name).
          </Step>
          <Step>
            <strong>Listen for the pattern.</strong> Three happy, three sad, one tense. Always
            in the same positions. Always.
          </Step>
        </Steps>
        <NoteImage
          src="/notes/music-theory/finals/05b-functional-harmony-tonic-pre-dominant.png"
          caption="Functional harmony at a glance — every diatonic chord lives in one of three families: Tonic (I, vi, iii — home), Pre-dominant (ii, IV — the journey out), Dominant (V, vii° — the pull back home). This is the engine that drives almost every chord progression in Western music."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-6"
        label="Chapter 6 · Piano Worm"
        why="Chords come from scales — and scales come from a visual pattern of white-and-black keys we call the piano worm. The next chapter teaches you to see scales without naming notes."
      />
    </LessonShell>
  );
}
